import { OperationResult } from '@monorepo/components/src/models/operation-result'
import { HomepageCarouselSlide, CarouselSearchItem } from '../models/homepage-carousel/homepage-carousel-slide'
import { http } from '@monorepo/infra'

const allowedUserGroups = [
  'eventVendorAdmin',
  'productVendorAdmin',
  'directvAdmin',
  'superAdmin',
  'vendorAdmin',
]

const allowedUserGroupsRegexes = allowedUserGroups.map(
  (x) => new RegExp(`^\\/?${x}$`, 'i')
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAllowedUser = (user?: any): boolean => {
  if (!Array.isArray(user?.groups)) {
    return false
  }

  try {
    return user.groups.some((x: string) =>
      allowedUserGroupsRegexes.some((y) => y.test(x))
    )

  } catch (error) {
    return false
  }
}

export const getChannelSlidesAsync = async (
    channel: string
): Promise<OperationResult<HomepageCarouselSlide[], string>> => {
    try {
        const res = await http.get<HomepageCarouselSlide[]>({
            url: `/homepage-carousel/admin/by-channel/${channel.toLowerCase()}`,
        })

        if (res?.status !== 200) {
            return { kind: 'error', errorData: 'Failed to load slides' }
        }

        return { kind: 'success', data: res.data ?? [] }
    } catch (error) {
        return { kind: 'error', errorData: `Error ${error}` }
    }
}

export const searchItemsAsync = async (
    search: string,
    channel: string
): Promise<OperationResult<CarouselSearchItem[], string>> => {
    try {
        const res = await http.get<CarouselSearchItem[]>({
            url: `/homepage-carousel/search-items`,
            params: { search, channel },
        })

        if (res?.status !== 200) {
            return { kind: 'error', errorData: 'Failed to search items' }
        }

        return { kind: 'success', data: res.data ?? [] }
    } catch (error) {
        return { kind: 'error', errorData: `Error ${error}` }
    }
}

export const updateSlideAsync = async (
    slideId: string,
    itemId: string,
    itemType: string
): Promise<OperationResult<HomepageCarouselSlide, string>> => {
    try {
        const res = await http.patch<HomepageCarouselSlide>({
            url: `/homepage-carousel/${slideId}`,
            data: { itemId, itemType },
        })

        if (res?.status !== 200) {
            return { kind: 'error', errorData: 'Failed to update slide' }
        }

        return { kind: 'success', data: res.data }
    } catch (error) {
        return { kind: 'error', errorData: `Error ${error}` }
    }
}

export const addSlidesAsync = async (
    channel: string,
    count: number
): Promise<OperationResult<HomepageCarouselSlide[], string>> => {
    try {
        const res = await http.post<HomepageCarouselSlide[]>({
            url: `/homepage-carousel/by-channel/${channel.toLowerCase()}`,
            data: { count },
        })

        if (res?.status !== 200 && res?.status !== 201) {
            return { kind: 'error', errorData: 'Failed to add slides' }
        }

        return { kind: 'success', data: res.data ?? [] }
    } catch (error) {
        return { kind: 'error', errorData: `Error ${error}` }
    }
}

export const deleteSlideAsync = async (
    slideId: string
): Promise<OperationResult<void, string>> => {
    try {
        const res = await http.delete<void>({
            url: `/homepage-carousel/${slideId}`,
        })
        // Backend responds with 204 No Content on success
        if (res?.status !== 200 && res?.status !== 204) {
            return { kind: 'error', errorData: 'Failed to delete slide' }
        }
        return { kind: 'success', data: undefined }
    } catch (error) {
        return { kind: 'error', errorData: `Error ${error}` }
    }
}

export const uploadSlideThumbnailAsync = async (
    slideId: string,
    file: File
): Promise<OperationResult<HomepageCarouselSlide, string>> => {
    try {
        const formData = new FormData()
        formData.append('file', file)

        const res = await http.post<HomepageCarouselSlide>({
            url: `/homepage-carousel/${slideId}/thumbnail`,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        })

        if (res?.status !== 200 && res?.status !== 201) {
            return { kind: 'error', errorData: 'Failed to upload thumbnail' }
        }

        return { kind: 'success', data: res.data }
    } catch (error) {
        return { kind: 'error', errorData: `Error ${error}` }
    }
}

export const clearSlideThumbnailAsync = async (
    slideId: string
): Promise<OperationResult<HomepageCarouselSlide, string>> => {
    try {
        const res = await http.delete<HomepageCarouselSlide>({
            url: `/homepage-carousel/${slideId}/thumbnail`,
        })

        if (res?.status !== 200) {
            return { kind: 'error', errorData: 'Failed to clear thumbnail' }
        }

        return { kind: 'success', data: res.data }
    } catch (error) {
        return { kind: 'error', errorData: `Error ${error}` }
    }
}

export const HomepageCarouselHelper = {
    getChannelSlidesAsync,
    searchItemsAsync,
    updateSlideAsync,
    addSlidesAsync,
    deleteSlideAsync,
    uploadSlideThumbnailAsync,
    clearSlideThumbnailAsync,
    isAllowedUser,
}
